// ============================================================================
// EML (RFC 5322 + MIME) PARSER
// ----------------------------------------------------------------------------
// Parses standard internet email files (.eml) into a structured shape:
//   {
//     headers: { from, to, cc, bcc, subject, date, replyTo, messageId },
//     body: {
//       plain: string,        // text/plain part decoded
//       html: string,         // text/html part decoded (Content-ID images
//                             // already remapped to data: URLs)
//     },
//     attachments: [{
//       filename, mimeType, size, isInline, contentId, dataUrl, blob
//     }],
//   }
//
// Designed for browser use — no Node Buffer, no node:zlib, no streaming.
// All work is done synchronously on a string + Uint8Array view of the file.
//
// Why we don't use a library:
// QuoteMaker ships as a pure-frontend SPA via a service worker. Adding
// `mailparser` would pull in 800kB+ of Node polyfills (iconv-lite, libmime,
// libqp, htmlparser2). The construction-quote use case only needs sender /
// recipient / subject / body / attachments — no MIME quirks like rare
// encodings, S/MIME signing, DKIM, or DSN parsing. ~600 lines covers it.
//
// References:
//   RFC 5322 — Internet Message Format (headers, folding, addresses)
//   RFC 2045 — MIME Part 1 (Content-Type, Content-Transfer-Encoding)
//   RFC 2046 — MIME Part 2 (multipart/* boundary syntax)
//   RFC 2047 — Encoded-Words in headers (=?utf-8?B?...?=)
//   RFC 2392 — cid: URL scheme (Content-ID inline images)
// ============================================================================

const MAX_BODY_BYTES = 5 * 1024 * 1024;   // 5 MB safety cap for any single part
const MAX_ATTACHMENTS = 50;               // parser stops adding after this
const MAX_PARTS = 200;                    // multipart recursion safety

// ─── Structured shapes ──────────────────────────────────────────────────────

export interface EmlTopLevelHeaders {
    from: string;
    to: string;
    cc: string;
    bcc: string;
    replyTo: string;
    subject: string;
    date: string;
    messageId: string;
}

export interface EmlAttachment {
    filename: string;
    mimeType: string;
    size: number;
    isInline: boolean;
    contentId: string;
    dataUrl: string;
    blob: Blob | null;
}

export interface ParsedEml {
    headers: EmlTopLevelHeaders;
    body: { plain: string; html: string };
    attachments: EmlAttachment[];
    /**
     * Present and true when the body this parse holds may NOT be the message's
     * whole body: a safety cap cut it (a text part past MAX_BODY_BYTES, more than
     * MAX_PARTS parts, parts nested deeper than the walker goes) or the MIME
     * structure is not closed (a declared multipart boundary that never appears,
     * or one with no closing delimiter, whose last part is then dropped). Absent
     * means none of those happened. Absence alone does not say a body was read:
     * use `emailBodyReadInFull`. It is never set for attachment caps.
     */
    incomplete?: boolean;
}

interface ParsedContentType {
    type: string;
    subtype: string;
    full: string;
    boundary: string;
    charset: string;
    name: string;
}

interface ParsedContentDisposition {
    type: string;
    filename: string;
}

interface SplitParamsResult {
    value: string;
    params: Record<string, string>;
}

interface HeaderBodySplit {
    headerEnd: number;
    bodyStart: number;
}

interface MultipartSegment {
    headerBytes: Uint8Array;
    bodyBytes: Uint8Array;
}

interface EmlAddress {
    name: string;
    email: string;
}

interface WalkPartArgs {
    headers: Map<string, string>;
    ctype: ParsedContentType;
    cte: string;
    contentId: string;
    contentDisposition: ParsedContentDisposition;
    bodyBytes: Uint8Array;
    result: ParsedEml;
    depth: number;
    partCounter: { count: number };
    /** The multipart this part sits in: its subtype and how many body-text leaves it holds directly. */
    container?: { subtype: string; bodyLeaves: number };
}

/**
 * Parse an EML buffer (ArrayBuffer or Uint8Array) into structured form.
 * Returns null only if the input is not parseable as an email at all.
 *
 * `input` is untrusted file bytes; the runtime `instanceof` guards are kept so
 * non-buffer inputs still return null.
 */
export function parseEmlBuffer(input: unknown): ParsedEml | null {
    const bytes = input instanceof ArrayBuffer
        ? new Uint8Array(input)
        : (input instanceof Uint8Array ? input : null);
    if (!bytes || bytes.length === 0) return null;

    // Headers are always 7-bit ASCII; the body may be 8-bit. Decode the
    // headers as latin1 (1 byte → 1 codepoint, lossless for ASCII), then
    // decode each MIME part with its declared charset.
    const fullText = bytesToLatin1(bytes);

    // Split headers from body at the first blank line. EML uses CRLF but
    // many real-world files use LF only — handle both. `findHeaderBodySplit`
    // returns either null (no blank line found — treat the whole input as
    // body, headers empty) or `{ headerEnd, bodyStart }` byte offsets.
    const splitAt = findHeaderBodySplit(fullText);
    const headerSection = splitAt ? fullText.slice(0, splitAt.headerEnd) : '';
    const bodyStart = splitAt ? splitAt.bodyStart : 0;

    const headers = parseHeaders(headerSection);
    const ctype = parseContentType(headers.get('content-type') || 'text/plain; charset=us-ascii');
    const cte = (headers.get('content-transfer-encoding') || '7bit').trim().toLowerCase();
    const cid = stripAngles(headers.get('content-id') || '');
    const cdisp = parseContentDisposition(headers.get('content-disposition') || '');

    const result: ParsedEml = {
        headers: extractTopLevelHeaders(headers),
        body: { plain: '', html: '' },
        attachments: [],
    };

    // Body bytes — slice from the original Uint8Array, NOT the latin1 string,
    // so that 8-bit binary attachments survive intact for base64 decoding.
    const bodyBytes = bytes.subarray(bodyStartByteOffset(bytes, splitAt));

    walkPart({
        headers,
        ctype,
        cte,
        contentId: cid,
        contentDisposition: cdisp,
        bodyBytes,
        result,
        depth: 0,
        partCounter: { count: 0 },
    });

    // After all parts collected, remap any Content-ID references inside
    // the HTML body to data: URLs from the matching inline attachments.
    if (result.body.html) {
        result.body.html = remapInlineImages(result.body.html, result.attachments);
    }

    return result;
}

// ─── Header parsing ────────────────────────────────────────────────────────

function parseHeaders(headerSection: string): Map<string, string> {
    // Unfold per RFC 5322 §2.2.3 — a continuation line starts with WSP.
    const unfolded = headerSection.replace(/\r?\n[ \t]+/g, ' ');
    const lines = unfolded.split(/\r?\n/);
    const map = new Map<string, string>();
    for (const line of lines) {
        if (!line) continue;
        const colon = line.indexOf(':');
        if (colon === -1) continue;
        const name = line.slice(0, colon).trim().toLowerCase();
        const value = line.slice(colon + 1).trim();
        if (!name) continue;
        // Multi-value headers (Received:, Authentication-Results:) — keep
        // the first occurrence we see for the headers we care about, but
        // don't lose the others (concat with newlines for completeness).
        if (map.has(name)) {
            map.set(name, map.get(name) + '\n' + value);
        } else {
            map.set(name, value);
        }
    }
    return map;
}

function extractTopLevelHeaders(map: Map<string, string>): EmlTopLevelHeaders {
    return {
        from:      decodeHeaderWords(map.get('from') || ''),
        to:        decodeHeaderWords(map.get('to') || ''),
        cc:        decodeHeaderWords(map.get('cc') || ''),
        bcc:       decodeHeaderWords(map.get('bcc') || ''),
        replyTo:   decodeHeaderWords(map.get('reply-to') || ''),
        subject:   decodeHeaderWords(map.get('subject') || ''),
        date:      (map.get('date') || '').trim(),
        messageId: stripAngles(map.get('message-id') || ''),
    };
}

// RFC 2047 encoded-word: =?charset?encoding?text?=
function decodeHeaderWords(raw: string): string {
    if (!raw) return '';
    return raw.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (_: string, charset: string, enc: string, text: string) => {
        try {
            let bytes: Uint8Array;
            if (enc.toUpperCase() === 'B') {
                bytes = base64ToBytes(text);
            } else {
                // Q encoding — like quoted-printable but '_' → space
                const qpText = text.replace(/_/g, ' ');
                bytes = qpDecodeToBytes(qpText);
            }
            return decodeBytes(bytes, charset);
        } catch {
            return text;
        }
    }).replace(/\?==\?[^?]+\?[BbQq]\?/g, ''); // join adjacent encoded-words
}

function stripAngles(s: string): string {
    return (s || '').trim().replace(/^<|>$/g, '');
}

// ─── Content-Type / Content-Disposition parsing ────────────────────────────

function parseContentType(raw: string): ParsedContentType {
    const parts = splitParams(raw);
    const value = (parts.value || 'text/plain').toLowerCase();
    const [type, subtype] = value.split('/');
    return {
        type: type || 'text',
        subtype: subtype || 'plain',
        full: value,
        boundary: parts.params.boundary || '',
        charset: (parts.params.charset || 'us-ascii').toLowerCase(),
        name: decodeHeaderWords(parts.params.name || ''),
    };
}

function parseContentDisposition(raw: string): ParsedContentDisposition {
    const parts = splitParams(raw);
    return {
        type: (parts.value || 'inline').toLowerCase(),
        filename: decodeHeaderWords(parts.params.filename || ''),
    };
}

// Parses "value; key1=v1; key2="quoted v2"" into { value, params: { key: v } }
// Handles RFC 2231 continuations (key*0=, key*1=) with naive concatenation.
function splitParams(raw: string): SplitParamsResult {
    const out: SplitParamsResult = { value: '', params: {} };
    if (!raw) return out;
    // First semicolon-separated chunk is the value; remaining are key=v pairs.
    const tokens = smartSplit(raw, ';');
    if (tokens.length === 0) return out;
    // tokens[0]/tokens[i] are in-bounds here (length checked / loop bound); the
    // non-null assertions are compile-erased and match the M3-S8 array-access
    // precedent (notes-markdown.ts).
    out.value = tokens[0]!.trim();
    for (let i = 1; i < tokens.length; i++) {
        const kv = tokens[i]!.trim();
        const eq = kv.indexOf('=');
        if (eq === -1) continue;
        let key = kv.slice(0, eq).trim().toLowerCase();
        let val = kv.slice(eq + 1).trim();
        // Strip RFC 2231 continuation suffix
        key = key.replace(/\*\d+(\*?)$/, '');
        // Strip surrounding quotes
        if (val.startsWith('"') && val.endsWith('"')) {
            val = val.slice(1, -1);
        }
        // RFC 2231 charset prefix: charset'lang'urlencoded
        if (val.includes("'")) {
            const m = val.match(/^([^']*)'([^']*)'(.*)$/);
            if (m) {
                try { val = decodeURIComponent(m[3]!); } catch { /* keep raw */ }
            }
        }
        if (out.params[key]) out.params[key] += val;
        else out.params[key] = val;
    }
    return out;
}

// Split on `sep` but respect double-quoted regions.
function smartSplit(s: string, sep: string): string[] {
    const out: string[] = [];
    let buf = '';
    let inQuotes = false;
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === '"') { inQuotes = !inQuotes; buf += ch; continue; }
        if (ch === sep && !inQuotes) { out.push(buf); buf = ''; continue; }
        buf += ch;
    }
    if (buf) out.push(buf);
    return out;
}

// ─── Body / part walking ───────────────────────────────────────────────────

function walkPart({ headers, ctype, cte, contentId, contentDisposition, bodyBytes, result, depth, partCounter, container }: WalkPartArgs): void {
    if (++partCounter.count > MAX_PARTS) { result.incomplete = true; return; }
    if (depth > 10) { result.incomplete = true; return; }

    if (ctype.type === 'multipart' && ctype.boundary) {
        const { parts, closed } = splitMultipart(bodyBytes, ctype.boundary);
        // A multipart that never opens or never closes is a damaged or cut-off
        // message: the part after the last delimiter is not read at all.
        if (!closed) result.incomplete = true;
        const ownContainer = { subtype: ctype.subtype, bodyLeaves: 0 };
        for (const part of parts) {
            const subHeaders = parseHeaders(bytesToLatin1(part.headerBytes));
            const subCtype = parseContentType(subHeaders.get('content-type') || 'text/plain');
            const subCte = (subHeaders.get('content-transfer-encoding') || '7bit').trim().toLowerCase();
            const subCid = stripAngles(subHeaders.get('content-id') || '');
            const subCdisp = parseContentDisposition(subHeaders.get('content-disposition') || '');
            walkPart({
                headers: subHeaders,
                ctype: subCtype,
                cte: subCte,
                contentId: subCid,
                contentDisposition: subCdisp,
                bodyBytes: part.bodyBytes,
                result,
                depth: depth + 1,
                partCounter,
                container: ownContainer,
            });
        }
        // Siblings that are NOT alternatives of one another are all part of the
        // message, but the summary renders one body. Alternatives (plain + html
        // of the same text) are one body in two forms and are not a loss.
        if (ownContainer.subtype !== 'alternative' && ownContainer.bodyLeaves > 1) result.incomplete = true;
        // A nested group that carries body text is ONE body to its parent, however
        // many representations it holds: counted once, so `mixed { alternative
        // { html }, plain }` is two distinct bodies (one is discarded) while
        // `mixed { alternative { plain, html }, attachment }` is one.
        if (container && ownContainer.bodyLeaves > 0) container.bodyLeaves += 1;
        return;
    }

    // Leaf part. Decide whether it's body text or an attachment.
    const decoded = decodePartBody(bodyBytes, cte);
    const isText = ctype.type === 'text';
    const dispType = (contentDisposition.type || 'inline').toLowerCase();
    const filename = contentDisposition.filename || ctype.name || '';
    const looksLikeAttachment =
        dispType === 'attachment' ||
        (filename && !isText) ||
        (!isText && contentId === '' && ctype.type !== 'multipart');

    if (isText && !looksLikeAttachment && (!filename || dispType === 'inline')) {
        // Body text — utf-8 / iso-8859-1 / etc per ctype.charset.
        const decodedText = decodeBytes(decoded, ctype.charset);
        if (decodedText.length > MAX_BODY_BYTES) result.incomplete = true;
        const text = decodedText.slice(0, MAX_BODY_BYTES);
        if (container) container.bodyLeaves += 1;
        if (ctype.subtype === 'html') {
            if (!result.body.html) result.body.html = text;
            else if (text.trim()) result.incomplete = true; // a second html body part is dropped
        } else {
            // text/plain or anything else text/*
            if (!result.body.plain) result.body.plain = text;
            else if (text.trim()) result.incomplete = true; // a second plain body part is dropped
        }
        return;
    }

    // Attachment (or inline image referenced by Content-ID).
    if (result.attachments.length >= MAX_ATTACHMENTS) return;
    const mimeType = ctype.full || 'application/octet-stream';
    const att = {
        filename: filename || synthesizeFilename(mimeType, contentId, result.attachments.length),
        mimeType,
        size: decoded.byteLength,
        isInline: dispType === 'inline' || !!contentId,
        contentId: contentId || '',
        // Build a data: URL for browser-side rendering. For very large
        // attachments this is wasteful but the MAX_BODY_BYTES cap keeps
        // it bounded. The blob option is also exposed for download links.
        dataUrl: bytesToDataUrl(decoded, mimeType),
        // `decoded` is an ArrayBuffer-backed Uint8Array at runtime; the cast
        // satisfies the BlobPart type (lib widens to Uint8Array<ArrayBufferLike>)
        // and is compile-erased.
        blob: typeof Blob !== 'undefined' ? new Blob([decoded as BlobPart], { type: mimeType }) : null,
    };
    result.attachments.push(att);
}

/**
 * The next GENUINE delimiter line for `boundary` at or after `from`: `--boundary`
 * (or `--boundary--` to close) that starts a line and is followed by nothing but
 * optional transport padding up to the line end (RFC 2046 §5.1.1). The same
 * characters inside a body line (`see --b-- below`) are text, not a delimiter,
 * and must neither end the part early nor claim the multipart closed.
 */
function findDelimiterLine(
    text: string,
    boundary: string,
    from: number,
): { start: number; after: number; closing: boolean } | null {
    const delim = '--' + boundary;
    let index = text.indexOf(delim, from);
    while (index !== -1) {
        if (index === 0 || text[index - 1] === '\n') {
            let after = index + delim.length;
            const closing = text.startsWith('--', after);
            if (closing) after += 2;
            let probe = after;
            while (text[probe] === ' ' || text[probe] === '\t') probe += 1;
            if (probe >= text.length || text[probe] === '\r' || text[probe] === '\n') {
                return { start: index, after: probe, closing };
            }
        }
        index = text.indexOf(delim, index + 1);
    }
    return null;
}

function splitMultipart(bytes: Uint8Array, boundary: string): { parts: MultipartSegment[]; closed: boolean } {
    // Boundary delimiters in MIME: a line containing exactly `--boundary`
    // (between parts) or `--boundary--` (final). We hunt for them in the
    // raw byte stream so binary parts survive without text corruption.
    // `closed` is true only when a genuine closing delimiter line was reached.
    const text = bytesToLatin1(bytes);
    const parts: MultipartSegment[] = [];
    let closed = false;
    // Skip the preamble.
    const first = findDelimiterLine(text, boundary, 0);
    if (!first) return { parts, closed };
    if (first.closing) return { parts, closed: true };
    let cursor = first.after;
    // Skip CRLF after boundary marker.
    if (text[cursor] === '\r') cursor++;
    if (text[cursor] === '\n') cursor++;

    while (cursor < text.length) {
        // Find the next delimiter line (a part boundary or the closing one).
        const next = findDelimiterLine(text, boundary, cursor);
        if (!next) break;
        const nextDelim = next.start;
        // Trim trailing CRLF before the boundary (per RFC 2046 §5.1.1).
        let endByte = nextDelim;
        if (text[endByte - 1] === '\n') endByte--;
        if (text[endByte - 1] === '\r') endByte--;
        const partBytes = bytes.subarray(cursor, endByte);
        // Split this part into headers / body at first blank line.
        const split = findHeaderBodySplit(bytesToLatin1(partBytes));
        if (split && split.headerEnd >= 0) {
            parts.push({
                headerBytes: partBytes.subarray(0, split.headerEnd),
                bodyBytes: partBytes.subarray(split.bodyStart),
            });
        } else {
            parts.push({
                headerBytes: new Uint8Array(0),
                bodyBytes: partBytes,
            });
        }
        // Was this the closing boundary?
        if (next.closing) { closed = true; break; }
        cursor = next.after;
        if (text[cursor] === '\r') cursor++;
        if (text[cursor] === '\n') cursor++;
    }
    return { parts, closed };
}

function findHeaderBodySplit(text: string): HeaderBodySplit | null {
    const crlf = text.indexOf('\r\n\r\n');
    if (crlf !== -1) return { headerEnd: crlf, bodyStart: crlf + 4 };
    const lf = text.indexOf('\n\n');
    if (lf !== -1) return { headerEnd: lf, bodyStart: lf + 2 };
    return null;
}

function bodyStartByteOffset(bytes: Uint8Array, split: HeaderBodySplit | null): number {
    if (!split) return 0;
    return split.bodyStart;
}

// ─── Encoding decoders ─────────────────────────────────────────────────────

function decodePartBody(bytes: Uint8Array, cte: string): Uint8Array {
    if (cte === 'base64') return base64ToBytes(bytesToLatin1(bytes).replace(/\s+/g, ''));
    if (cte === 'quoted-printable') return qpDecodeToBytes(bytesToLatin1(bytes));
    // 7bit, 8bit, binary — pass through as-is.
    return bytes;
}

function base64ToBytes(str: string): Uint8Array {
    if (!str) return new Uint8Array(0);
    // Strip whitespace + non-base64 noise.
    const clean = str.replace(/[^A-Za-z0-9+/=]/g, '');
    try {
        const binary = atob(clean);
        const out = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
        return out;
    } catch {
        return new Uint8Array(0);
    }
}

function qpDecodeToBytes(str: string): Uint8Array {
    // Quoted-printable: =XX hex escapes, soft line breaks ("=\r\n" or "=\n").
    if (!str) return new Uint8Array(0);
    const collapsed = str.replace(/=\r?\n/g, '');
    const out: number[] = [];
    for (let i = 0; i < collapsed.length; i++) {
        const ch = collapsed[i]!;  // in-bounds (loop bound)
        if (ch === '=' && i + 2 < collapsed.length) {
            const hex = collapsed.slice(i + 1, i + 3);
            if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
                out.push(parseInt(hex, 16));
                i += 2;
                continue;
            }
        }
        out.push(ch.charCodeAt(0) & 0xff);
    }
    return Uint8Array.from(out);
}

function decodeBytes(bytes: Uint8Array, charset: string): string {
    const cs = (charset || 'utf-8').toLowerCase().replace(/[_]/g, '-');
    // Browser TextDecoder supports 'utf-8', 'iso-8859-1', 'windows-1252', etc.
    // Normalise common email charset aliases.
    const aliasMap: Record<string, string> = {
        'us-ascii': 'utf-8',
        'ascii': 'utf-8',
        'utf8': 'utf-8',
        'cp1252': 'windows-1252',
        'iso-8859-1': 'iso-8859-1',
        'latin1': 'iso-8859-1',
    };
    const alias = aliasMap[cs] || cs;
    try {
        return new TextDecoder(alias, { fatal: false }).decode(bytes);
    } catch {
        try {
            return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
        } catch {
            return bytesToLatin1(bytes);
        }
    }
}

function bytesToLatin1(bytes: Uint8Array): string {
    // 1-byte-per-codepoint string; preserves binary data losslessly so we
    // can use String#indexOf on byte sequences for boundary detection.
    let s = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        s += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
    }
    return s;
}

function bytesToDataUrl(bytes: Uint8Array, mimeType: string): string {
    if (!bytes || bytes.length === 0) return '';
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
    }
    try {
        return `data:${mimeType};base64,${btoa(binary)}`;
    } catch {
        return '';
    }
}

function synthesizeFilename(mimeType: string, contentId: string, idx: number): string {
    const extMap: Record<string, string> = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/heic': 'heic',
        'application/pdf': 'pdf',
        'application/zip': 'zip',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/vnd.ms-excel': 'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'text/plain': 'txt',
        'text/html': 'html',
    };
    const ext = extMap[mimeType.toLowerCase()] || 'bin';
    if (contentId) {
        const safeCid = contentId.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 40);
        return `${safeCid}.${ext}`;
    }
    return `attachment-${idx + 1}.${ext}`;
}

// ─── Content-ID inline image resolution ────────────────────────────────────

function remapInlineImages(html: string, attachments: EmlAttachment[]): string {
    if (!html || !attachments?.length) return html;
    return html.replace(/(["'])cid:([^"'>\s]+)\1/gi, (full: string, q: string, cid: string) => {
        const cleanCid = decodeURIComponent(cid).trim();
        const match = attachments.find(a => a.contentId === cleanCid);
        if (match && match.dataUrl) return `${q}${match.dataUrl}${q}`;
        return full;
    }).replace(/\bsrc\s*=\s*cid:([^"'>\s]+)/gi, (full: string, cid: string) => {
        const cleanCid = decodeURIComponent(cid).trim();
        const match = attachments.find(a => a.contentId === cleanCid);
        if (match && match.dataUrl) return `src="${match.dataUrl}"`;
        return full;
    });
}

// ─── Pretty helpers consumers can use ──────────────────────────────────────

/**
 * Format an address-list header value into renderable parts.
 * "Jane Doe <jane@example.com>, Bob <bob@example.com>"
 *   → [{ name, email }]
 */
export function parseAddressList(raw: string): EmlAddress[] {
    if (!raw) return [];
    return smartSplit(raw, ',')
        .map(s => parseSingleAddress(s.trim()))
        .filter((a): a is EmlAddress => a !== null);
}

function parseSingleAddress(s: string): EmlAddress | null {
    if (!s) return null;
    const m = s.match(/^(.*?)\s*<([^>]+)>\s*$/);
    if (m) return { name: cleanQuotes(m[1]!.trim()), email: m[2]!.trim() };
    if (s.includes('@')) return { name: '', email: s.trim() };
    return { name: s, email: '' };
}

function cleanQuotes(s: string): string {
    if (!s) return '';
    if (s.startsWith('"') && s.endsWith('"')) return s.slice(1, -1);
    return s;
}

/**
 * Render the email into a single-screen friendly text summary suitable
 * for the existing text-extraction pipeline (Stage 2 risk analysis etc).
 */
export function emailToPlainSummary(parsed: ParsedEml | null): string {
    if (!parsed) return '';
    const h = parsed.headers || {} as EmlTopLevelHeaders;
    const lines: string[] = [];
    if (h.from)    lines.push(`From: ${h.from}`);
    if (h.to)      lines.push(`To: ${h.to}`);
    if (h.cc)      lines.push(`CC: ${h.cc}`);
    if (h.date)    lines.push(`Date: ${h.date}`);
    if (h.subject) lines.push(`Subject: ${h.subject}`);
    const body = parsed.body?.plain
        || htmlToPlainText(parsed.body?.html || '')
        || '';
    return lines.length > 0
        ? `${lines.join('\n')}\n\n${body}`
        : body;
}

/** The readable body text exactly as `emailToPlainSummary` renders it, without the header block. */
export function emailBodyText(parsed: ParsedEml | null): string {
    if (!parsed) return '';
    return (parsed.body?.plain || htmlToPlainText(parsed.body?.html || '') || '').trim();
}

/**
 * Can the parser stand behind "the body I hold is the message's whole body"?
 * Both halves are required: the structure was read to its end (no safety cap
 * fired, every declared multipart opened and closed) AND there is readable body
 * text. A header-only message, a multipart whose boundary never appears and a
 * cut-off message all answer false, so a header block can never stand in for a
 * body.
 */
export function emailBodyReadInFull(parsed: ParsedEml | null): boolean {
    return !!parsed && parsed.incomplete !== true && emailBodyText(parsed).length > 0;
}

export function htmlToPlainText(html: string): string {
    if (!html) return '';
    return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<\/li>/gi, '\n')
        .replace(/<\/tr>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
